/**
 * Text fitting that mirrors Inchbug's production print script:
 *   1. all lines form one block, baselines `leading` em apart,
 *   2. the block's INK bounds (not the em box) are measured,
 *   3. the block is scaled uniformly so the ink fits the text box,
 *   4. the ink centre is placed on the text box centre.
 *
 * All metrics are per 1 px of font size, so the scale returned by fitScale()
 * is the font size in px. Pure functions except measureLines (needs a 2D ctx).
 * Loaded as a plain <script defer> before vue-canvas.js (window.LabelTextFit).
 * tests/ require() it for the side effect and read globalThis.LabelTextFit —
 * assets/package.json is "type": "module", so module.exports would be ignored.
 */
(function (root) {
  const REF_SIZE = 100;

  const LabelTextFit = {
    /**
     * Measure each line's ink bounds at a reference size.
     * @returns {{asc:number, desc:number, left:number, right:number}[]} unit metrics
     */
    measureLines(ctx, fontFamily, lines) {
      ctx.save();
      ctx.font = `${REF_SIZE}px ${fontFamily}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      const metrics = lines.map((line) => {
        const m = ctx.measureText(line);
        return {
          asc: m.actualBoundingBoxAscent / REF_SIZE,
          desc: m.actualBoundingBoxDescent / REF_SIZE,
          left: m.actualBoundingBoxLeft / REF_SIZE,
          right: m.actualBoundingBoxRight / REF_SIZE,
        };
      });
      ctx.restore();
      return metrics;
    },

    /**
     * The font's line box per 1 px of size ({ascent, descent}, as Illustrator sizes
     * a point-text frame), or null where the browser lacks fontBoundingBox metrics.
     */
    measureFont(ctx, fontFamily) {
      ctx.save();
      ctx.font = `${REF_SIZE}px ${fontFamily}`;
      ctx.textBaseline = 'alphabetic';
      const m = ctx.measureText('Hg');
      ctx.restore();
      if (!(m.fontBoundingBoxAscent > 0)) {
        return null;
      }
      return { ascent: m.fontBoundingBoxAscent / REF_SIZE, descent: m.fontBoundingBoxDescent / REF_SIZE };
    },

    /**
     * Where print moves the text to its target and THEN resizes it, Illustrator
     * scales around the text frame's centre, not the ink's, so the ink ends up
     * (fontSize - frameSize) x (ink centre - frame centre) away from the target.
     * Returns that offset in px: dy vertically (frame = first ascent to last
     * descent), dx horizontally (frame centre = advance centre of centred text).
     * frameSize is the template frame's size in px; font from measureFont.
     */
    resizeDrift(ink, font, leading, lineCount, frameSize, fontSize) {
      if (!font || !(frameSize > 0)) {
        return { dx: 0, dy: 0 };
      }
      const frameCentreY = (-font.ascent + (lineCount - 1) * leading + font.descent) / 2;
      const inkCentreY = (ink.top + ink.bottom) / 2;
      const inkCentreX = (ink.right - ink.left) / 2;
      const growth = fontSize - frameSize;
      return { dx: growth * inkCentreX, dy: growth * (inkCentreY - frameCentreY) };
    },

    /**
     * Stack the lines and return the union ink box in unit space.
     * Baseline of line i sits at i * leading; every line is centred on x = 0.
     */
    layoutInk(metrics, leading) {
      let top = Infinity;
      let bottom = -Infinity;
      let left = 0;
      let right = 0;
      const baselines = [];
      for (const [i, m] of metrics.entries()) {
        const y = i * leading;
        baselines.push(y);
        top = Math.min(top, y - m.asc);
        bottom = Math.max(bottom, y + m.desc);
        left = Math.max(left, m.left);
        right = Math.max(right, m.right);
      }
      return { top, bottom, left, right, width: left + right, height: bottom - top, baselines };
    },

    /**
     * Uniform scale (= font size in px) that fits the ink into the box.
     * caps.em caps the font size; caps.inkHeight caps the scaled ink height.
     */
    fitScale(ink, box, caps = {}) {
      if (!(ink.width > 0) || !(ink.height > 0)) {
        return 0;
      }
      let scale = Math.min(box.width / ink.width, box.height / ink.height);
      if (caps.inkHeight) {
        scale = Math.min(scale, caps.inkHeight / ink.height);
      }
      if (caps.em) {
        scale = Math.min(scale, caps.em);
      }
      return scale;
    },

    /**
     * Where to draw: centre x, one baseline per line, and the resulting ink box.
     * Vertically the ink centre goes on the box centre (print moves the text to
     * TextBB). Horizontally print keeps the template frame's centre, so pass
     * anchorX (canvas px) when known; otherwise the box centre is used.
     */
    placeBlock(ink, scale, box, anchorX) {
      const cx = typeof anchorX === 'number' ? anchorX : box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      const inkCentre = ((ink.top + ink.bottom) / 2) * scale;
      const baseline0 = cy - inkCentre;
      return {
        x: cx,
        baselines: ink.baselines.map((b) => baseline0 + b * scale),
        inkBox: {
          x: cx - ink.left * scale,
          y: baseline0 + ink.top * scale,
          width: ink.width * scale,
          height: ink.height * scale,
        },
      };
    },

    /** A placed block moved by (dx, dy) px. */
    shiftPlaced(placed, dx, dy) {
      return {
        x: placed.x + dx,
        baselines: placed.baselines.map((b) => b + dy),
        inkBox: { ...placed.inkBox, x: placed.inkBox.x + dx, y: placed.inkBox.y + dy },
      };
    },

    /** Die-cut fractions → canvas px inside an area rectangle. */
    boxFromSpec(fraction, area) {
      return {
        x: area.x + fraction.x0 * area.width,
        y: area.y + fraction.y0 * area.height,
        width: (fraction.x1 - fraction.x0) * area.width,
        height: (fraction.y1 - fraction.y0) * area.height,
      };
    },

    /**
     * Line pitch in em. A font's custom leading applies only to the print products
     * listed in spec.leading.products; with no product code it always applies.
     */
    leadingFor(spec, fontName, productCode) {
      const custom = fontName && spec.leading.byFontName[fontName];
      if (!custom) {
        return spec.leading.default;
      }
      if (productCode && spec.leading.products && !spec.leading.products.includes(productCode)) {
        return spec.leading.default;
      }
      return custom;
    },

    /** Caps in px for this font, given the area's px-per-point. Empty object when none apply. */
    capsFor(spec, fontName, pxPerPt) {
      const caps = {};
      if (!fontName) {
        return caps;
      }
      const em = spec.caps.emPtByFontName[fontName];
      const inkHeight = spec.caps.inkHeightPtByFontName[fontName];
      if (em) {
        caps.em = em * pxPerPt;
      }
      if (inkHeight) {
        caps.inkHeight = inkHeight * pxPerPt;
      }
      return caps;
    },

    /**
     * The print template for this Area: the product code names the template folder;
     * bundle codes map each Area size to the product the print script splits it into.
     * The print script opens templates/<product>/<set>.ai, where <set> is the icon
     * set name, or the colour name for products without icons; pass both (in that
     * order) and the first matching per-file override is applied.
     * Returns the template plus its product code as `name` and the matched file as
     * `set`, or null when there is no template for this size (fallback: spec.labels).
     */
    resolveTemplate(spec, productCode, size, setNames = []) {
      if (!productCode || !size) {
        return null;
      }
      const combo = spec.combos[productCode];
      const code = combo ? combo[size] : productCode;
      const template = code ? spec.templates[code] : null;
      if (!template || template.size !== size) {
        return null;
      }
      const { sets, ...base } = template;
      for (const setName of setNames) {
        const key = LabelTextFit.setKey(setName);
        if (key && sets && sets[key]) {
          return { name: code, set: key, ...base, ...sets[key] };
        }
      }
      return { name: code, set: null, ...base };
    },

    /** Template file key for an icon set or colour name: lower-case letters and digits only. */
    setKey(name) {
      return typeof name === 'string' ? name.toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    },

    /**
     * Where to draw a whole image so its ink rectangle (image px) contain-fits
     * the target box, centred; with fit 'fill' the whole image fills the box.
     */
    iconDrawRect(image, ink, box, fit) {
      if (fit === 'fill' || !ink || !(ink.width > 0) || !(ink.height > 0)) {
        return { x: box.x, y: box.y, width: box.width, height: box.height };
      }
      const scale = Math.min(box.width / ink.width, box.height / ink.height);
      const inkX = box.x + (box.width - ink.width * scale) / 2;
      const inkY = box.y + (box.height - ink.height * scale) / 2;
      return {
        x: inkX - ink.x * scale,
        y: inkY - ink.y * scale,
        width: image.width * scale,
        height: image.height * scale,
      };
    },

    /**
     * Opaque bounds of a loaded image in its own pixels ({x, y, width, height}),
     * cached per image. Null until the image has loaded or if it cannot be read.
     */
    imageInkBounds(img) {
      if (!img || !img.complete || !img.naturalWidth) {
        return null;
      }
      if (INK_CACHE.has(img)) {
        return INK_CACHE.get(img);
      }
      let bounds = null;
      try {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const alpha = ctx.getImageData(0, 0, width, height).data;
        let x0 = width;
        let y0 = height;
        let x1 = -1;
        let y1 = -1;
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            if (alpha[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
        }
        bounds = x1 >= 0 ? { x: x0, y: y0, width: x1 - x0 + 1, height: y1 - y0 + 1 } : null;
      } catch (error) {
        bounds = null;
      }
      INK_CACHE.set(img, bounds);
      return bounds;
    },
  };

  /**
   * The two label bodies inside a ShoePal pair artwork (image px), found from the
   * alpha channel: top/bottom from a column near each outer edge (clear of the
   * split icon), left/right edges along the middle row. Cached; null if unreadable.
   */
  LabelTextFit.shoePalArtBodies = (img) => {
    if (!img || !img.complete || !img.naturalWidth) {
      return null;
    }
    if (BODY_CACHE.has(img)) {
      return BODY_CACHE.get(img);
    }
    let bodies = null;
    try {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, width, height).data;
      const opaque = (x, y) => data[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD;
      const half = Math.floor(width / 2);
      const body = (probeX, fromCentre, toEdge) => {
        let top = 0;
        while (top < height && !opaque(probeX, top)) top++;
        let bottom = height - 1;
        while (bottom > top && !opaque(probeX, bottom)) bottom--;
        const midY = Math.round((top + bottom) / 2);
        let inner = fromCentre;
        while (inner !== toEdge && !opaque(inner, midY)) inner += Math.sign(toEdge - fromCentre);
        let outer = toEdge;
        while (outer !== inner && !opaque(outer, midY)) outer -= Math.sign(toEdge - fromCentre);
        const x0 = Math.min(inner, outer);
        const x1 = Math.max(inner, outer);
        return { x: x0, y: top, width: x1 - x0 + 1, height: bottom - top + 1 };
      };
      const probe = Math.round(width * 0.15);
      bodies = [body(probe, half, 0), body(width - 1 - probe, half, width - 1)];
    } catch (error) {
      bodies = null;
    }
    BODY_CACHE.set(img, bodies);
    return bodies;
  };

  /** Reads a loaded image's alpha once; returns (x, y) => boolean, or null if unreadable. */
  const alphaReader = (img) => {
    if (!img || !img.complete || !img.naturalWidth) {
      return null;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      return (x, y) => data[(y * canvas.width + x) * 4 + 3] > ALPHA_THRESHOLD;
    } catch (error) {
      return null;
    }
  };

  /**
   * The back (plain, lower-right) disc of a circle bag tag artwork, in image px:
   * {cx, cy, d}. Its vertical extent at the right edge gives the centre row; the
   * lowest opaque pixel on the right side gives the radius. Cached; null if unreadable.
   */
  LabelTextFit.circleBagTagBackDisc = (img) => {
    if (!img || !img.complete || !img.naturalWidth) {
      return null;
    }
    if (DISC_CACHE.has(img)) {
      return DISC_CACHE.get(img);
    }
    const opaque = alphaReader(img);
    let disc = null;
    if (opaque) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      let first = -1;
      let last = -1;
      for (let y = 0; y < height; y++) {
        if (opaque(width - 1, y) || opaque(width - 2, y)) {
          if (first < 0) first = y;
          last = y;
        }
      }
      let bottom = -1;
      for (let y = height - 1; y >= 0 && bottom < 0; y--) {
        for (let x = Math.round(width * 0.6); x < width; x += 2) {
          if (opaque(x, y)) {
            bottom = y;
            break;
          }
        }
      }
      if (first >= 0 && bottom >= 0) {
        const cy = (first + last) / 2;
        const radius = bottom - cy;
        disc = { cx: width - 1 - radius, cy, d: 2 * radius };
      }
    }
    DISC_CACHE.set(img, disc);
    return disc;
  };

  /**
   * The front (upper) tag of a rectangle bag tag artwork, in image px: the opaque
   * block above the first fully transparent row band. Cached; null if unreadable.
   */
  LabelTextFit.rectangleBagTagFront = (img) => {
    if (!img || !img.complete || !img.naturalWidth) {
      return null;
    }
    if (FRONT_CACHE.has(img)) {
      return FRONT_CACHE.get(img);
    }
    const opaque = alphaReader(img);
    let front = null;
    if (opaque) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const rowEmpty = (y) => {
        for (let x = 0; x < width; x += 3) {
          if (opaque(x, y)) return false;
        }
        return true;
      };
      let top = 0;
      while (top < height && rowEmpty(top)) top++;
      let bottom = top;
      while (bottom < height && !rowEmpty(bottom)) bottom++;
      const midY = Math.round((top + bottom) / 2);
      let left = 0;
      while (left < width && !opaque(left, midY)) left++;
      let right = width - 1;
      while (right > left && !opaque(right, midY)) right--;
      if (bottom > top && right > left) {
        front = { x: left, y: top, width: right - left + 1, height: bottom - top };
      }
    }
    FRONT_CACHE.set(img, front);
    return front;
  };

  /**
   * Sample points on the silhouette of a loaded image (image px): the outermost
   * opaque pixel from each side on a grid of rows and columns. Cached per image;
   * an empty list if the image cannot be read.
   */
  LabelTextFit.imageInkEdgePoints = (img) => {
    if (!img || !img.complete || !img.naturalWidth) {
      return [];
    }
    if (EDGE_CACHE.has(img)) {
      return EDGE_CACHE.get(img);
    }
    const points = [];
    const opaque = alphaReader(img);
    if (opaque) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const stepX = Math.max(1, Math.floor(width / EDGE_SAMPLES));
      const stepY = Math.max(1, Math.floor(height / EDGE_SAMPLES));
      for (let y = 0; y < height; y += stepY) {
        let first = 0;
        while (first < width && !opaque(first, y)) first++;
        if (first === width) continue;
        let last = width - 1;
        while (!opaque(last, y)) last--;
        points.push({ x: first, y }, { x: last, y });
      }
      for (let x = 0; x < width; x += stepX) {
        let first = 0;
        while (first < height && !opaque(x, first)) first++;
        if (first === height) continue;
        let last = height - 1;
        while (!opaque(x, last)) last--;
        points.push({ x, y: first }, { x, y: last });
      }
    }
    EDGE_CACHE.set(img, points);
    return points;
  };

  // Pixels at or below this alpha are treated as transparent padding, not ink.
  const ALPHA_THRESHOLD = 20;
  const INK_CACHE = new WeakMap();
  const EDGE_CACHE = new WeakMap();
  const EDGE_SAMPLES = 80;
  const BODY_CACHE = new WeakMap();
  const DISC_CACHE = new WeakMap();
  const FRONT_CACHE = new WeakMap();

  root.LabelTextFit = LabelTextFit;
})(typeof window !== 'undefined' ? window : globalThis);
